/**
 * SettingsPage — H07
 *
 * Three tabs:
 *  1. Cuenta de marca  — name, plan chip, website, API key (mock)
 *  2. Notificaciones   — email alert preferences (toggles)
 *  3. Facturación      — plan card, usage bar, mock invoice history
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Building2,
    Bell,
    CreditCard,
    Copy,
    Check,
    Globe,
    Mail,
    MessageCircle,
    Zap,
    Key,
    BarChart3,
    AlertTriangle,
    CheckCircle2,
    Circle,
    Sparkles,
    LogOut,
} from "lucide-react";
import { useIntegracion } from "../../../hooks/useIntegracion";
import { useAuth } from "../../../hooks/useAuth";
import { toast } from "../../../shared/hooks/useToast";

type SettingsTab = "brand" | "notifications" | "billing";

const TABS: { key: SettingsTab; label: string; icon: typeof Building2 }[] = [
    { key: "brand", label: "Cuenta de marca", icon: Building2 },
    { key: "notifications", label: "Notificaciones", icon: Bell },
    { key: "billing", label: "Facturación", icon: CreditCard },
];

export function SettingsPage() {
    const [tab, setTab] = useState<SettingsTab>("brand");
    const { logout } = useAuth();
    const navigate = useNavigate();
    const [loggingOut, setLoggingOut] = useState(false);

    // Audit B-03: Settings no tenía forma de cerrar sesión.
    const handleLogout = async () => {
        if (loggingOut) return;
        setLoggingOut(true);
        try {
            await logout();
        } finally {
            navigate("/login", { replace: true });
        }
    };

    return (
        <div className="min-h-screen p-6 md:p-8 animate-fade-in">
            <div className="mx-auto max-w-3xl space-y-6">
                {/* Header */}
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h1 className="text-4xl font-bold text-slate-900 tracking-tight">
                            Configuración
                        </h1>
                        <p className="text-slate-500 text-base mt-1">
                            Ajustá tu cuenta, preferencias de alertas y facturación.
                        </p>
                    </div>
                    <button
                        onClick={handleLogout}
                        disabled={loggingOut}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-colors disabled:opacity-50 flex-shrink-0"
                    >
                        <LogOut size={15} />
                        {loggingOut ? "Cerrando…" : "Cerrar sesión"}
                    </button>
                </div>

                {/* Tab nav */}
                <div className="flex items-center gap-1 border-b border-slate-200">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        const active = tab === t.key;
                        return (
                            <button
                                key={t.key}
                                onClick={() => setTab(t.key)}
                                className={`relative flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                                    active
                                        ? "text-violet-700"
                                        : "text-slate-500 hover:text-slate-800"
                                }`}
                            >
                                <Icon size={15} />
                                {t.label}
                                {active && (
                                    <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-violet-600 rounded-t-full" />
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Tab content */}
                {tab === "brand" && <BrandTab />}
                {tab === "notifications" && <NotificationsTab />}
                {tab === "billing" && <BillingTab />}
            </div>
        </div>
    );
}

/* ─── TAB 1: Cuenta de marca ─── */
function BrandTab() {
    const { marca } = useIntegracion();
    const [copied, setCopied] = useState(false);

    const mockApiKey = "dnx_live_sk_" + "a3f8b9c2d1e4f7g6h5i0j2k1l3m4n5";

    const copyKey = () => {
        navigator.clipboard.writeText(mockApiKey).catch(() => null);
        setCopied(true);
        toast.success("API key copiada al portapapeles");
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="space-y-4">
            {/* Brand info card */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                <div className="flex items-start gap-5">
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center overflow-hidden flex-shrink-0 shadow-sm">
                        {marca?.appLogo ? (
                            <img
                                src={marca.appLogo}
                                alt={marca.appName}
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <Building2 size={28} className="text-white" />
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-xl font-bold text-slate-900 truncate">
                                {marca?.appName || (
                                    <span className="inline-block w-40 h-6 bg-slate-100 rounded animate-pulse" />
                                )}
                            </h3>
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200 uppercase tracking-wider flex-shrink-0">
                                Pro
                            </span>
                        </div>
                        {(marca as any)?.website && (
                            <div className="flex items-center gap-1.5 text-sm text-slate-500 mb-1">
                                <Globe size={13} />
                                <a
                                    href={(marca as any).website}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="hover:text-violet-600 hover:underline transition-colors"
                                >
                                    {(marca as any).website.replace(/^https?:\/\//, "")}
                                </a>
                            </div>
                        )}
                        <p className="text-xs text-slate-400">
                            Cuenta activa desde enero 2025
                        </p>
                    </div>
                </div>

                <div className="mt-5 pt-5 border-t border-slate-100">
                    <p className="text-xs text-slate-400 text-center">
                        Para cambiar el nombre, logo o sitio web, editá tu perfil
                        en{" "}
                        <Link
                            to="/brand?tab=identity"
                            className="font-semibold text-violet-600 hover:underline"
                        >
                            Manual de tu IA → Identidad
                        </Link>
                        .
                    </p>
                </div>
            </div>

            {/* API Key */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                    <Key size={15} className="text-slate-400" />
                    <h3 className="font-semibold text-slate-900 text-sm">
                        API Key
                    </h3>
                    <span className="text-[10px] font-medium text-slate-400 ml-auto">
                        Solo para integraciones custom
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex-1 flex items-center gap-2 px-3 py-2.5 bg-slate-50 rounded-xl border border-slate-200 min-w-0">
                        <span className="font-mono text-xs text-slate-700 truncate">
                            {mockApiKey.slice(0, 24)}••••••••••
                        </span>
                    </div>
                    <button
                        onClick={copyKey}
                        className="px-3 py-2.5 rounded-xl border border-slate-200 text-slate-500 hover:text-violet-600 hover:border-violet-300 hover:bg-violet-50 transition-all text-xs font-medium flex items-center gap-1.5 flex-shrink-0"
                    >
                        {copied ? (
                            <>
                                <Check size={13} className="text-emerald-600" />
                                Copiado
                            </>
                        ) : (
                            <>
                                <Copy size={13} />
                                Copiar
                            </>
                        )}
                    </button>
                </div>
                <p className="text-[11px] text-slate-400 mt-2">
                    Tratá este key como una contraseña. No lo compartas ni lo
                    incluyas en código público.
                </p>
            </div>

            {/* Danger zone */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                    <AlertTriangle size={15} className="text-red-500" />
                    <h3 className="font-semibold text-slate-900 text-sm">
                        Zona peligrosa
                    </h3>
                </div>
                <p className="text-xs text-slate-500 mb-4">
                    Estas acciones son irreversibles. Consultá con el equipo de
                    Deenex antes de continuar.
                </p>
                <div className="flex flex-wrap gap-2">
                    <button
                        disabled
                        aria-disabled="true"
                        className="px-4 py-2 rounded-lg text-xs font-semibold border border-red-200 text-red-600 bg-white opacity-60 cursor-not-allowed"
                    >
                        Exportar todos los datos
                    </button>
                    <button
                        disabled
                        aria-disabled="true"
                        className="px-4 py-2 rounded-lg text-xs font-semibold border border-red-200 text-red-700 bg-red-50 opacity-60 cursor-not-allowed"
                    >
                        Eliminar cuenta
                    </button>
                </div>
                <p className="text-[11px] text-slate-400 mt-3 flex items-center gap-1.5">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-300" />
                    Estas acciones requieren contactar al equipo de Deenex en{" "}
                    <a
                        href="mailto:soporte@deenex.io"
                        className="text-violet-600 hover:underline"
                    >
                        soporte@deenex.io
                    </a>
                </p>
            </div>
        </div>
    );
}

/* ─── TAB 2: Notificaciones ─── */
const ALERT_PREFS = [
    {
        id: "campaign_approved",
        icon: CheckCircle2,
        iconColor: "text-emerald-600",
        label: "Campaña aprobada por IA",
        description: "Cuando la IA aprueba una revisión de campaña custom",
        defaultOn: true,
    },
    {
        id: "campaign_needs_changes",
        icon: AlertTriangle,
        iconColor: "text-amber-600",
        label: "Campaña requiere cambios",
        description: "Cuando la revisión detecta observaciones que hay que corregir",
        defaultOn: true,
    },
    {
        id: "suggestion_available",
        icon: Sparkles,
        iconColor: "text-violet-600",
        label: "Nueva sugerencia de campaña",
        description: "Cuando Deenex propone una campaña nueva basada en oportunidades",
        defaultOn: true,
    },
    {
        id: "campaign_paused",
        icon: Circle,
        iconColor: "text-slate-500",
        label: "Campaña pausada automáticamente",
        description: "Cuando la IA pausa una campaña por conflicto o spam risk",
        defaultOn: false,
    },
    {
        id: "weekly_report",
        icon: BarChart3,
        iconColor: "text-blue-600",
        label: "Reporte semanal de performance",
        description: "Resumen del lunes con métricas de la semana anterior",
        defaultOn: true,
    },
    {
        id: "wa_connected",
        icon: MessageCircle,
        iconColor: "text-emerald-600",
        label: "Estado de cuentas WhatsApp",
        description: "Alertas de reconexión si una cuenta pierde vinculación",
        defaultOn: true,
    },
];

function NotificationsTab() {
    const [prefs, setPrefs] = useState<Record<string, boolean>>(
        Object.fromEntries(ALERT_PREFS.map((p) => [p.id, p.defaultOn])),
    );
    const [saved, setSaved] = useState(false);

    const toggle = (id: string) => {
        setPrefs((prev) => ({ ...prev, [id]: !prev[id] }));
        setSaved(false);
    };

    const handleSave = () => {
        setSaved(true);
        toast.success("Preferencias guardadas");
        setTimeout(() => setSaved(false), 2000);
    };

    return (
        <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                    <div>
                        <h3 className="font-semibold text-slate-900 text-sm">
                            Alertas por email
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Se envían a{" "}
                            <span className="font-medium text-slate-700">
                                admin@mirestaurante.com
                            </span>
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Mail size={14} className="text-slate-400" />
                        <span className="text-xs text-slate-500">Email</span>
                    </div>
                </div>

                <div className="divide-y divide-slate-50">
                    {ALERT_PREFS.map((pref) => {
                        const Icon = pref.icon;
                        const on = prefs[pref.id];
                        return (
                            <div
                                key={pref.id}
                                className="px-6 py-4 flex items-center justify-between gap-4"
                            >
                                <div className="flex items-start gap-3 min-w-0">
                                    <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <Icon
                                            size={14}
                                            className={pref.iconColor}
                                        />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-slate-900">
                                            {pref.label}
                                        </p>
                                        <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                                            {pref.description}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => toggle(pref.id)}
                                    className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors duration-200 ${
                                        on ? "bg-violet-600" : "bg-slate-200"
                                    }`}
                                    role="switch"
                                    aria-checked={on}
                                >
                                    <span
                                        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                                            on ? "translate-x-5" : "translate-x-0"
                                        }`}
                                    />
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="flex justify-end">
                <button
                    onClick={handleSave}
                    className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                        saved
                            ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                            : "bg-violet-600 text-white shadow-sm hover:bg-violet-700"
                    }`}
                >
                    {saved ? (
                        <span className="flex items-center gap-1.5">
                            <Check size={14} /> Guardado
                        </span>
                    ) : (
                        "Guardar preferencias"
                    )}
                </button>
            </div>
        </div>
    );
}

/* ─── TAB 3: Facturación ─── */
const INVOICES = [
    { date: "01/05/2026", amount: "$49.00", status: "Pagado", plan: "Pro" },
    { date: "01/04/2026", amount: "$49.00", status: "Pagado", plan: "Pro" },
    { date: "01/03/2026", amount: "$49.00", status: "Pagado", plan: "Pro" },
];

const PLAN_FEATURES = [
    "Campañas ilimitadas",
    "Hasta 50 000 mensajes / mes",
    "3 cuentas WhatsApp",
    "Revisión IA con iteraciones",
    "Segmentación por comportamiento",
    "Soporte prioritario",
];

function BillingTab() {
    const usedMessages = 12_847;
    const limitMessages = 50_000;
    const usagePct = (usedMessages / limitMessages) * 100;

    return (
        <div className="space-y-4">
            {/* Plan card */}
            <div className="bg-gradient-to-br from-violet-600 to-fuchsia-600 rounded-2xl p-6 text-white shadow-lg">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <p className="text-sm font-medium text-white/70">
                            Plan actual
                        </p>
                        <h3 className="text-3xl font-bold mt-0.5">Pro</h3>
                    </div>
                    <div className="text-right">
                        <p className="text-sm text-white/70">Facturación mensual</p>
                        <p className="text-2xl font-bold">$49</p>
                        <p className="text-xs text-white/60">USD / mes</p>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    {PLAN_FEATURES.map((f) => (
                        <div key={f} className="flex items-center gap-1.5 text-xs text-white/80">
                            <Check size={12} className="text-white/60 flex-shrink-0" />
                            {f}
                        </div>
                    ))}
                </div>
                <div className="mt-4 pt-4 border-t border-white/20 flex items-center justify-between">
                    <span className="text-xs text-white/70">
                        Próximo cobro: 1/06/2026
                    </span>
                    <a
                        href="mailto:soporte@deenex.io?subject=Cambio%20de%20plan"
                        className="text-xs font-semibold text-white/90 hover:text-white underline underline-offset-2"
                    >
                        Cambiar plan
                    </a>
                </div>
            </div>

            {/* Usage */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                    <Zap size={15} className="text-violet-500" />
                    <h3 className="font-semibold text-slate-900 text-sm">
                        Uso del mes
                    </h3>
                    <span className="ml-auto text-xs text-slate-400 font-mono">
                        {usedMessages.toLocaleString("es-AR")} /{" "}
                        {limitMessages.toLocaleString("es-AR")} mensajes
                    </span>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div
                        className="h-full rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-all duration-700"
                        style={{ width: `${usagePct}%` }}
                    />
                </div>
                <p className="text-xs text-slate-400 mt-2">
                    {usagePct.toFixed(1)}% del límite mensual usado ·{" "}
                    <span className="text-slate-600 font-medium">
                        {(limitMessages - usedMessages).toLocaleString("es-AR")}{" "}
                        disponibles
                    </span>
                </p>
            </div>

            {/* Invoice history */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100">
                    <h3 className="font-semibold text-slate-900 text-sm">
                        Historial de pagos
                    </h3>
                </div>
                <div className="divide-y divide-slate-50">
                    {INVOICES.map((inv, i) => (
                        <div
                            key={i}
                            className="px-6 py-4 flex items-center justify-between"
                        >
                            <div className="flex items-center gap-3">
                                <CreditCard
                                    size={14}
                                    className="text-slate-400"
                                />
                                <div>
                                    <p className="text-sm font-medium text-slate-900">
                                        Plan {inv.plan} · {inv.date}
                                    </p>
                                    <p className="text-xs text-slate-400">
                                        Visa •••• 4242
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
                                    {inv.status}
                                </span>
                                <span className="text-sm font-bold text-slate-900">
                                    {inv.amount}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default SettingsPage;
